import { $isAtNodeEnd } from '@lexical/selection';
import { type ElementNode, type TextNode, type RangeSelection } from 'lexical';
import FAIcon, { IconStyle } from '../../Icons/Icons';
import { AdditionBtnStyle } from '../types/enums';
import { type ToolbarBtnListType } from '../types/propsType';
import styles from '../RichTextEditor.module.scss';
// Editor Theme
const rteTheme = {
  ltr: 'ltr',
  rtl: 'rtl',
  paragraph: 'editor-paragraph',
  quote: 'editor-quote',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
    h3: 'editor-heading-h3',
    h4: 'editor-heading-h4',
    h5: 'editor-heading-h5',
    h6: 'editor-heading-h6',
  },
  list: {
    nested: {
      listitem: 'editor-nested-listitem',
    },
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listItem',
    listitemChecked: 'editor-listItemChecked',
    listitemUnchecked: 'editor-listItemUnchecked',
  },
  hashtag: 'editor-hashtag',
  image: 'editor-image',
  link: 'editor-link',
  text: {
    bold: 'editor-textBold',
    code: styles['editor-textCode'],
    italic: 'editor-textItalic',
    strikethrough: styles['editor-textStrikethrough'],
    subscript: styles['editor-textSubscript'],
    superscript: styles['editor-textSuperscript'],
    underline: styles['editor-textUnderline'],
    underlineStrikethrough: styles['editor-textUnderlineStrikethrough'],
  },
  codeHighlight: {
    atrule: 'editor-tokenAttr',
    attr: 'editor-tokenAttr',
    boolean: 'editor-tokenProperty',
    builtin: 'editor-tokenSelector',
    cdata: 'editor-tokenComment',
    char: 'editor-tokenSelector',
    class: 'editor-tokenFunction',
    'class-name': 'editor-tokenFunction',
    comment: 'editor-tokenComment',
    constant: 'editor-tokenProperty',
    deleted: 'editor-tokenProperty',
    doctype: 'editor-tokenComment',
    entity: 'editor-tokenOperator',
    function: 'editor-tokenFunction',
    important: 'editor-tokenVariable',
    inserted: 'editor-tokenSelector',
    keyword: 'editor-tokenAttr',
    namespace: 'editor-tokenVariable',
    number: 'editor-tokenProperty',
    operator: 'editor-tokenOperator',
    prolog: 'editor-tokenComment',
    property: 'editor-tokenProperty',
    punctuation: 'editor-tokenPunctuation',
    regex: 'editor-tokenVariable',
    selector: 'editor-tokenSelector',
    string: 'editor-tokenSelector',
    symbol: 'editor-tokenProperty',
    tag: 'editor-tokenProperty',
    url: 'editor-tokenOperator',
    variable: 'editor-tokenVariable',
  },
  table: styles['editor-table'],
  tableAddColumns: styles['editor-tableAddColumns'],
  tableAddRows: styles['editor-tableAddRows'],
  tableCell: styles['editor-tableCell'],
  tableCellActionButton: styles['editor-tableCellActionButton'],
  tableCellActionButtonContainer: styles['editor-tableCellActionButtonContainer'],
  tableCellEditing: styles['editor-tableCellEditing'],
  tableCellHeader: styles['editor-tableCellHeader'],
  tableCellPrimarySelected: styles['editor-tableCellPrimarySelected'],
  tableCellResizer: styles['editor-tableCellResizer'],
  tableCellSelected: styles['editor-tableCellSelected'],
  tableCellSortedIndicator: styles['editor-tableCellSortedIndicator'],
  tableResizeRuler: styles['editor-tableCellResizeRuler'],
  tableSelected: styles['editor-tableSelected'],
  tableSelection: styles['editor-tableSelection'],
};
const toolbarBtnList: ToolbarBtnListType[] = [
  {
    label: <FAIcon iconName="code" />,
    style: AdditionBtnStyle.HTMLCODE,
    divider: true,
  },
  {
    label: <FAIcon iconName="undo" />,
    style: AdditionBtnStyle.Undo,
  },
  {
    label: <FAIcon iconName="redo" />,
    style: AdditionBtnStyle.Redo,
    divider: true,
  },
  {
    label: <FAIcon iconName="bold" iconStyle={IconStyle.SOLID} />,
    style: 'bold',
  },
  {
    label: <FAIcon iconName="italic" />,
    style: 'italic',
  },
  {
    label: <FAIcon iconName="strikethrough" />,
    style: 'strikethrough',
  },
  {
    label: <FAIcon iconName="underline" />,
    style: 'underline',
    divider: true,
  },
  {
    label: <FAIcon iconName="highlighter" />,
    style: 'highlight',
  },
  {
    label: <FAIcon iconName="code-simple" />,
    style: 'code',
  },
  {
    label: <FAIcon iconName="subscript" />,
    style: 'subscript',
  },
  {
    label: <FAIcon iconName="superscript" />,
    style: 'superscript',
    divider: true,
  },
  {
    label: <FAIcon iconName="align-left" />,
    style: AdditionBtnStyle.left,
  },
  {
    label: <FAIcon iconName="align-center" />,
    style: AdditionBtnStyle.center,
  },
  {
    label: <FAIcon iconName="align-right" />,
    style: AdditionBtnStyle.right,
  },
  {
    label: <FAIcon iconName="list-ul" />,
    style: AdditionBtnStyle.Listul,
  },
  {
    label: <FAIcon iconName="list-ol" />,
    style: AdditionBtnStyle.Listol,
    divider: true,
  },
  {
    label: <FAIcon iconName="link" />,
    style: AdditionBtnStyle.Link,
  },
  {
    label: <FAIcon iconName="table" />,
    style: AdditionBtnStyle.Table,
  },
  {
    label: <FAIcon iconName="image" />,
    style: AdditionBtnStyle.Image,
  },
  // {
  //   label: <FAIcon iconName="smile" />,
  //   style: AdditionBtnStyle.Emoji,
  // },
];
const blockTypeToBlockName = {
  code: 'Code Block',
  h1: 'Heading 1',
  h2: 'Heading 2',
  h3: 'Heading 3',
  h4: 'Heading 4',
  h5: 'Heading 5',
  h6: 'Heading 6',
  paragraph: 'Normal',
  quote: 'Quote',
};
const listTypeToListName = {
  bullet: 'bullet',
  number: 'number',
};
const rootTypeToRootName = {
  root: 'Root',
  table: 'Table',
};

function getSelectedNode(selection: RangeSelection): TextNode | ElementNode {
  const { anchor } = selection;
  const { focus } = selection;
  const anchorNode = selection.anchor.getNode();
  const focusNode = selection.focus.getNode();
  if (anchorNode === focusNode) {
    return anchorNode;
  }
  const isBackward = selection.isBackward();
  if (isBackward) {
    return $isAtNodeEnd(focus) ? anchorNode : focusNode;
  } else {
    return $isAtNodeEnd(anchor) ? anchorNode : focusNode;
  }
}

export { rteTheme, getSelectedNode, listTypeToListName, toolbarBtnList, blockTypeToBlockName, rootTypeToRootName };
